<template>
   <div>
      <h6 class="sub-heading">Force-Install via Google Admin Console (ChromeOS &amp; Managed Chrome)</h6>
      <p>If your organisation uses Google Workspace with managed Chrome browsers or Chromebooks, you can force-install XSurfLog directly from the Google Admin Console — no Group Policy needed.</p>

      <h6 class="sub-heading mt-4">Step 1: Open the Google Admin Console</h6>
      <ol class="step-list">
         <li>Sign in to <a href="https://admin.google.com" target="_blank">admin.google.com</a> with your administrator account.</li>
         <li>Navigate to <strong>Devices → Chrome → Apps &amp; extensions</strong>.</li>
         <li>Select the organisational unit (OU) you want to deploy XSurfLog to, or apply it to the top-level org to deploy to everyone.</li>
      </ol>

      <h6 class="sub-heading mt-4">Step 2: Add the XSurfLog Extension</h6>
      <ol class="step-list">
         <li>Click the <strong>+</strong> (Add) button in the bottom-right corner.</li>
         <li>Select <strong>"Add from Chrome Web Store"</strong>.</li>
         <li>Search for <strong>"XSurf Log"</strong> or paste the extension ID:</li>
      </ol>
      <div class="code-block">
         <code>mipgaapolfgfbonecakhhacpmafffogo</code>
      </div>
      <ol class="step-list mt-2" start="4">
         <li>Click the extension from the search results and then click <strong>Select</strong>.</li>
      </ol>

      <h6 class="sub-heading mt-4">Step 3: Set the Installation Policy</h6>
      <ol class="step-list">
         <li>Once added, click on the XSurfLog extension in the list.</li>
         <li>Under <strong>Installation policy</strong>, choose one of the following:
            <ul class="sub-list">
               <li><strong>Force install:</strong> Automatically installs and prevents the user from removing it. (Recommended)</li>
               <li><strong>Force install + pin to toolbar:</strong> Same as above, but also pins the icon to the browser toolbar for easy access.</li>
               <li><strong>Allow install:</strong> Makes the extension available in the Chrome Web Store but lets the user decide whether to install it.</li>
            </ul>
         </li>
         <li>Click <strong>Save</strong>.</li>
      </ol>

      <div class="info-box info-box-blue mb-3 mt-3">
         <i class="fa-solid fa-circle-info me-2"></i>
         <strong>Tip:</strong> We recommend using <strong>"Force install + pin to toolbar"</strong> so users can easily see and interact with the XSurfLog extension icon at all times.
      </div>

      <h6 class="sub-heading mt-4">Step 4: Verify Deployment</h6>
      <p>Changes typically take effect within <strong>24 hours</strong>, but you can speed it up by having a user restart their Chrome browser. To verify:</p>
      <ol class="step-list">
         <li>Open Chrome on a managed device.</li>
         <li>Navigate to <code>chrome://extensions</code>.</li>
         <li>Confirm that XSurfLog appears in the list and is enabled.</li>
         <li>Check that the XSurfLog icon is visible in the toolbar (if pinned).</li>
      </ol>

      <div class="info-box info-box-yellow mb-3 mt-3">
         <i class="fa-solid fa-triangle-exclamation me-2"></i>
         <strong>Note:</strong> For ChromeOS devices, the extension will be installed at the user-level when they sign in with their managed Google account. Device-level policies require enrolling the Chromebook in your organisation's management.
      </div>

      <p class="mt-3 ref-text">
         <i class="fa-solid fa-link me-1"></i> Reference:
         <a href="https://support.google.com/chrome/a/answer/6306504" target="_blank">
            Google Support — Automatically install apps and extensions
         </a>
      </p>
   </div>
</template>

<style scoped>
.sub-heading {
   font-weight: 700; font-size: 15px; color: #435666;
}

p, li { font-size: 14px; color: #435666; line-height: 1.7; }

.code-block {
   background: #f5f6f8; border: 1px solid #e9ecef;
   border-radius: 8px; padding: 12px 16px;
   font-family: monospace; font-size: 13px;
   word-break: break-all; color: #d63384;
}

.info-box {
   border-radius: 8px; padding: 12px 16px;
   font-size: 13px; line-height: 1.6;
}
.info-box-blue { background: #e7f6f9; border-left: 4px solid #00A0C1; color: #435666; }
.info-box-yellow { background: #fff8e1; border-left: 4px solid #ffc107; color: #435666; }

.step-list { padding-left: 20px; }
.step-list li { margin-bottom: 6px; }
.sub-list { padding-left: 20px; margin-top: 6px; }
.sub-list li { margin-bottom: 4px; }

a { color: #00A0C1; text-decoration: none; }
a:hover { text-decoration: underline; }

.ref-text { font-size: 13px; color: #6c757d; }
</style>
